import { postMfaCodeResend, postMfaVerification } from 'shared/api/authApi'
import { getApiErrorCode, getApiErrorMessage, getApiErrorStatus } from 'shared/utils/apiError'
import { t } from 'shared/i18n'

/**
 * MFA（メールで届く確認コード）の確認。
 * 認証途中の状態は `api_auth_attempt` Cookie で運ばれるため、コードだけを送る。
 */
export async function submitMfaCode (code: string): Promise<void> {
  await postMfaVerification(code)
}

/** 確認コードを再送する。 */
export async function resendMfaCode (): Promise<void> {
  await postMfaCodeResend()
}

/** 確認コード送信の失敗を画面向けに写した結果。 */
export interface MfaErrorView {
  message: string
  /** 認証途中の状態が失効しており、ログインからやり直す必要があるか。 */
  restartLogin: boolean
}

/**
 * MFA 確認コード送信で出たエラーを、ユーザー向け日本語メッセージへ写す。
 *
 * - 401 `mfa_code_invalid`: コード不一致。api の `detail`（「認証コードが正しくありません」）を出す。
 *   コードは失効しないので同じ画面で再入力させる
 * - 401 それ以外: 途中状態（`api_auth_attempt`）が無い・期限切れ・消費済み。ログインからやり直す。
 *   文言は Cognito 時代にセッション失効（`Invalid session for the user.`）へ出していたものと同じ
 * - 429: 試行上限に達しロック中
 */
export function mapConfirmMfaError (error: unknown): MfaErrorView {
  const status = getApiErrorStatus(error)

  if (status === 401 && getApiErrorCode(error) === 'mfa_code_invalid') {
    return { message: getApiErrorMessage(error) ?? t('shared.mfa.message3'), restartLogin: false }
  }
  if (status === 401) {
    return { message: t('shared.mfa.message'), restartLogin: true }
  }
  if (status === 429) {
    return { message: t('shared.mfa.message'), restartLogin: false }
  }
  return { message: t('shared.mfa.message3'), restartLogin: false }
}
